import { useState } from 'react'

interface UseGuardedCloseArgs {
  /** True while closing would lose edits; the discard warning shows first. */
  hasUnsavedEdits: boolean
  onClose: () => void
}

export interface GuardedClose {
  /**
   * Close, or ask first when there are unsaved edits. `afterClose` runs only
   * once the screen actually closes (e.g. a breadcrumb's navigation).
   */
  requestClose: (afterClose?: () => void) => void
  /** Whether the discard warning is open. */
  isConfirmOpen: boolean
  /** DISCARD: close, then run the pending `afterClose`. */
  confirmDiscard: () => void
  /** KEEP EDITING: stay, and forget the pending `afterClose`. */
  cancelDiscard: () => void
}

/**
 * The close guard every full-screen player screen shares (Close, BACK, Cancel
 * and the breadcrumb): warn before discarding edits, close at once otherwise.
 */
export const useGuardedClose = ({
  hasUnsavedEdits,
  onClose,
}: UseGuardedCloseArgs): GuardedClose => {
  'use memo'

  // Null while no warning is open; otherwise what to run after DISCARD.
  const [pending, setPending] = useState<{ afterClose?: () => void } | null>(
    null
  )

  return {
    requestClose: (afterClose) => {
      if (hasUnsavedEdits) {
        setPending({ afterClose })
        return
      }
      onClose()
      afterClose?.()
    },
    isConfirmOpen: pending !== null,
    confirmDiscard: () => {
      const afterClose = pending?.afterClose
      setPending(null)
      onClose()
      afterClose?.()
    },
    cancelDiscard: () => setPending(null),
  }
}
